import type { PagedList } from 'contensis-core-api';
import type { Entry, PageEntry, Node } from 'contensis-delivery-api';
import cmsParser from 'Common/util/cmsParser';
import camelcase from 'camelcase';

import type { Props } from './Listing';
import type { FilterGroup, ActiveFilter, Filter } from 'Modules/Filters/CourseListing';
import type { ToggleProps } from 'Elements/Input/Toggle';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
    filters: FilterGroup[];
    toggle: ToggleProps;
    courses: PagedList<Entry>;
    courseVariants: Entry[];
    activeFilterQueries: Record<FilterTypes, QueryFilter[] | null>;
    activeAcademicYear?: string;
    searchTerm: string | null;
};

type FilterTypes = 'studyLevels' | 'courseOptions' | 'courseCategories';

export type QueryFilter = { id: string };

type CourseFeature =
    | 'professionalAccreditation'
    | 'additionalQualification'
    | 'professionalMemberships'
    | 'sandwichYear'
    | 'workExperiencePlacement'
    | 'studyAbroad'
    | 'placementYear'
    | 'foundationYear'
    | 'topUpOption'
    | 'furtherStudies'
    | 'specialistFacilities';

const featuresList = [
    'foundationYear',
    'placementYear',
    'professionalAccreditation',
    'workExperiencePlacement',
    'specialistFacilities'
];

const getCourseLevel = (contentTypeId: string): string => {
    switch (contentTypeId) {
        case 'coursePostgraduate':
            return 'Postgraduate';
        case 'courseApprenticeships':
            return 'Apprenticeships';
        case 'courseProfessional':
            return 'Professional';
        default:
            return 'Undergraduate';
    }
};

const datamap = (data: PageData): Props => {
    const {
        entry,
        ancestors,
        filters,
        toggle,
        activeFilterQueries,
        activeAcademicYear,
        courses,
        courseVariants,
        searchTerm
    } = data;

    const { heading, promoPoint, title, sys } = entry;

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: { heading: heading },
        filters: {
            id: 'course-listing-filters',
            filters: filters,
            initialActiveFilters: Object.keys(activeFilterQueries)
                .map((key: FilterTypes) => {
                    const activeFilters = activeFilterQueries[key];

                    if (activeFilters) {
                        return activeFilters.map(activeFilter => ({
                            ...activeFilter,
                            type: key,
                            label: (
                                (
                                    filters.find(filter => filter.type === key) as FilterGroup
                                ).options.find(option => option.id === activeFilter.id) as Filter
                            ).label
                        })) as ActiveFilter[];
                    }
                    return [];
                })
                .flat(),
            initialSearchTerm: searchTerm,
            toggle: {
                ...toggle,
                defaultValue: activeAcademicYear
            },
            numberOfResults: courses.totalCount
        },
        courses: {
            ...courses,
            items: courses.items.map(course => {
                const variants = courseVariants.filter(
                    variant => variant.container.sys.id === course.sys.id
                );
                const defaultVariant = variants.find(variant => variant.defaultVariant);

                const studyLevel: string | undefined =
                    course.courseListingFilterOptions?.studyLevel || undefined;
                const courseOptions: string[] =
                    course.courseListingFilterOptions?.courseOptions || [];

                const degreePrefix =
                    studyLevel && course.degreeType && course.degreeType !== 'none'
                        ? course.degreeType
                        : course.sys.contentTypeId === 'courseFoundationYears'
                        ? 'Foundation year'
                        : courseOptions.includes('Apprenticeship')
                        ? 'Apprenticeship'
                        : courseOptions.includes('Professional')
                        ? 'Professional'
                        : '';

                return {
                    id: course.sys.id,
                    degreeType: degreePrefix,
                    courseName: course.name,
                    link:
                        activeAcademicYear === entry.academicYearFilters[0].key ||
                        !activeAcademicYear
                            ? course.sys.uri
                            : defaultVariant
                            ? `${course.sys.uri}/${defaultVariant.year.name}/${defaultVariant.sys.slug}`
                            : course.sys.uri,
                    studyLevel: studyLevel,
                    categories: course.courseCategory.map(category => category.name),
                    variants:
                        variants.length > 0
                            ? variants
                                  .filter(variant => variant.container)
                                  .filter(variant => !variant.defaultVariant)
                                  .map(variant => ({
                                      id: variant.sys.id,
                                      label: variant.name,
                                      ariaLabel: variant.name,
                                      href: `${course.sys.uri}/${variant.year.name}/${variant.sys.slug}`
                                  }))
                            : undefined,
                    image: {
                        src: cmsParser.image(course.courseCardImage).src
                    },
                    status: course.state ? course.state.toLowerCase() : 'open',
                    compareCourseData: {
                        id: course.sys.id,
                        degreeType: degreePrefix,
                        name: course.name,
                        image: cmsParser.image(course.courseCardImage),
                        level: getCourseLevel(course.sys.contentTypeId),
                        entryRequirements: course.compareEntryRequirements
                            ? course.compareEntryRequirements
                            : '',
                        features: <Record<CourseFeature, boolean>>Object.fromEntries(
                            featuresList.map(feature => {
                                const courseCompareFeatures = course.compareFeatures
                                    ? course.compareFeatures.map((feature: string) =>
                                          camelcase(feature)
                                      )
                                    : null;

                                return [
                                    feature,
                                    courseCompareFeatures
                                        ? courseCompareFeatures.includes(feature)
                                        : false
                                ];
                            })
                        ),
                        link: { href: course.sys.uri }
                    }
                };
            })
        },
        promoCards: promoPoint[0].value.mobileImage
            ? {
                  heading: promoPoint[0].value.heading,
                  image: {
                      desktop: cmsParser.image(promoPoint[0].value.tabletDesktopImage),
                      mobile: cmsParser.image(promoPoint[0].value.mobileImage)
                  },
                  text: promoPoint[0].value.text,
                  link: promoPoint[0].value.link && cmsParser.linkButton(promoPoint[0].value.link)
              }
            : {
                  heading: promoPoint[0].value.heading,
                  text: promoPoint[0].value.description,
                  image: cmsParser.image(promoPoint[0].value.image),
                  link: cmsParser.linkButton(promoPoint[0].value.buttonCTA),
                  backgroundColor: cmsParser.getThemeColour(promoPoint[0].value.backgroundColour)
              }
    };
};

export default datamap;
